/**
 * WHERE "THE OTHER APP" LIVES — the one URL that survives the apex swap.
 *
 * Four places in this client link out to the FastAPI-served single-page app: three "Request
 * access" CTAs (marketing/sections.tsx twice, marketing/LandingPage.tsx once) and the
 * "No account yet?" line on the login screen. Every one of them was hard-coded to
 * `https://valubrief.ai/`.
 *
 * That is correct today and wrong the moment the apex swap happens. Per the 2026-08-08 plan,
 * `next.valubrief.ai` becomes `valubrief.ai` and the current app moves to `old.valubrief.ai`.
 * After the flip those four buttons point at the page the visitor is already standing on: a
 * "Request access" CTA that scrolls to the top of itself. Nothing errors, nothing logs, and the
 * only symptom is that the primary conversion path silently stops going anywhere.
 *
 * So it is derived from the hostname rather than pinned, and the swap needs no code change:
 *
 *   served from valubrief.ai      -> this IS the new app, the legacy app is old.valubrief.ai
 *   served from anywhere else     -> the legacy app is still valubrief.ai
 *
 * The second branch covers next.valubrief.ai (today), localhost, and Render previews, all of
 * which want the current, live apex.
 *
 * Deliberately a derived module constant and NOT `import.meta.env`, for the reason flags.ts
 * states: an env value can be changed from the Render dashboard with nobody reading a diff.
 *
 * ORDERING NOTE FOR THE SWAP. This function starts returning `old.valubrief.ai` the instant the
 * apex serves this bundle, so `old.valubrief.ai` must exist and answer BEFORE the apex DNS moves,
 * not after. That is step 1 of the cutover for a reason.
 */

/** The apex hostname the NEW client will be served from once the swap lands. */
const NEW_APP_HOST = "valubrief.ai";

/** Where the FastAPI single-page app lives after the swap. */
const LEGACY_HOST_AFTER_SWAP = "old.valubrief.ai";

export function legacyAppUrl(): string {
  const host =
    typeof window !== "undefined" && window.location ? window.location.hostname.toLowerCase() : "";
  // `www.valubrief.ai` redirects to the apex on Render, so treat it as the apex too.
  const onApex = host === NEW_APP_HOST || host === `www.${NEW_APP_HOST}`;
  return onApex ? `https://${LEGACY_HOST_AFTER_SWAP}/` : `https://${NEW_APP_HOST}/`;
}
